// src/pages/UserAllJoining.js

import React, {
  useState,
  useEffect,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  FiEdit,
  FiTrash2,
  FiDownload,
  FiX,
  FiSearch,
} from "react-icons/fi";

import {
  FaUserTie,
  FaBuilding,
  FaCalendarAlt,
  FaMoneyBillWave,
} from "react-icons/fa";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

import {
  getData,
  postData,
} from "../../../services/FetchNodeservices";

/* ================================================= */
/* USER */
/* ================================================= */

const getLoggedUser = () => {

  try {

    return JSON.parse(
      localStorage.getItem(
        "USER_INFO"
      )
    ) || null;

  } catch (error) { // Catch the error to prevent app crash on bad JSON
    console.error("Failed to parse USER_INFO from localStorage:", error);
    return null;
  }
};

const user =
  getLoggedUser() || {

    id: 0,

    first_name:
      "Unknown",

    last_name: "",
  };

/* ================================================= */
/* COMPONENT */
/* ================================================= */

export default function UserAllJoining() {
  /* ================================================= */
  /* LOCATION */
  /* ================================================= */

  const location =
    useLocation();

  /* ================================================= */
  /* STATES */
  /* ================================================= */

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [openModal, setOpenModal] =
    useState(false);

  // editingId will store s_no of the placement_tracker entry
  const [editingId, setEditingId] =
    useState(null);

  const [page, setPage] =
    useState(1);

  const rowsPerPage = 10;

  const [filters, setFilters] =
    useState({

      search: "",

      company: "",

      position: "",

      status: "",

      doj: "",
    });

  // formData now explicitly includes candidate_id if available,
  // which is crucial for updating the candidate table from the backend.
  const [formData, setFormData] =
    useState({

      s_no: null, // The primary key for placement_tracker entry
      candidate_id: null, // The ID of the linked candidate, if any

      month: "",

      candidate_name: "",

      phone: "",

      company: "",

      position: "",

      designation: "",

      doj: "",

      ctc_offered: "",

      percentage: "",

      amount: "",

      status_joined: "",

      entered_by:
        `${user.first_name} ${user.last_name}`,

      user_id:
        user.id,
    });

  /* ================================================= */
  /* LOAD DATA */
  /* ================================================= */

  useEffect(() => {

    fetchData();

  }, []);

  /* ================================================= */
  /* AUTO TODAY FILTER */
  /* ================================================= */

  useEffect(() => {

    const params =
      new URLSearchParams(
        location.search
      );

    const filter =
      params.get("filter");

    if (
      filter === "today"
    ) {

      setFilters((prev) => ({

        ...prev,

        doj:
          dayjs().format(
            "YYYY-MM-DD"
          ),
      }));
    }

  }, [location.search]);

  const fetchData =
    async () => {

      try {

        setLoading(true);

        const result =
          await getData(
            "placementtracker/fetch"
          );

        if (
          result?.status
        ) {

          // Ensure status_joined is always a string and uppercase for consistency
          const formatted =

            (result.data || [])
              .map(
                (item) => ({

                  ...item,
                  // Use item.status_joined (from pt table) primarily,
                  // fall back to candidate_current_status (from c table) if null.
                  // The backend was updated to provide COALESCE(pt.status_joined, c.status) AS status_joined
                  // so simply using item.status_joined should now be reliable.
                  status_joined:
                    String(item.status_joined || "").toUpperCase(),
                })
              );

          setRows(
            formatted
          );

        } else {
          // Handle backend status: false explicitly
          Swal.fire(
            "Error",
            result?.message || "Failed to load data.",
            "error"
          );
        }

      } catch (err) { // Catch actual network/JS errors
        console.error("Fetch data error:", err);
        Swal.fire(
          "Error",
          "Failed to load data. Please check network or server.",
          "error"
        );

      } finally {

        setLoading(false);

      }

    };

  /* ================================================= */
  /* DROPDOWNS */
  /* ================================================= */

  const companies =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.company
        )
      ),
    ].filter(Boolean); // Filter out null/undefined values

  const positions =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.position
        )
      ),
    ].filter(Boolean); // Filter out null/undefined values


  /* ================================================= */
  /* FILTER */
  /* ================================================= */

  const filteredData =
    rows.filter(
      (row) => {

        const search =
          filters.search.toLowerCase();

        const matchesSearch =

          row.candidate_name
            ?.toLowerCase()
            .includes(
              search
            ) ||

          row.company
            ?.toLowerCase()
            .includes(
              search
            ) ||

          row.position
            ?.toLowerCase()
            .includes(
              search
            );

        const matchesCompany =

          !filters.company ||

          row.company ===
          filters.company;

        const matchesPosition =

          !filters.position ||

          row.position ===
          filters.position;

        const matchesStatus =

          !filters.status ||

          // Use the cleaned status_joined from the state directly
          String(row.status_joined || "")
            .toUpperCase()
            .trim()

          ===

          filters.status
            .toUpperCase();


        const matchesDate =

          !filters.doj ||

          dayjs(
            row.doj
          ).format(
            "YYYY-MM-DD"
          ) ===
          filters.doj;

        return (

          matchesSearch &&
          matchesCompany &&
          matchesPosition &&
          matchesStatus &&
          matchesDate
        );
      }
    );

  /* ================================================= */
  /* PAGINATION */
  /* ================================================= */

  const paginated =
    filteredData.slice(

      (page - 1) *
      rowsPerPage,

      page *
      rowsPerPage
    );

  /* ================================================= */
  /* RESET FILTER */
  /* ================================================= */

  const resetFilters =
    () => {

      setFilters({

        search: "",

        company: "",

        position: "",

        status: "",

        doj: "",
      });
      setPage(1); // Reset page on filter reset
    };

  /* ================================================= */
  /* EXPORT EXCEL */
  /* ================================================= */

  const exportToExcel =
    () => {

      // Map data to ensure consistent column names for export
      const dataToExport = filteredData.map(r => ({
        "S.No": r.s_no,
        "Candidate ID": r.candidate_id,
        "User ID": r.user_id,
        "Month": r.month,
        "Candidate Name": r.candidate_name,
        "Phone": r.phone,
        "Company": r.company,
        "Position": r.position,
        "Designation": r.designation,
        "Date of Joining": r.doj ? dayjs(r.doj).format("YYYY-MM-DD") : "-",
        "CTC Offered": r.ctc_offered,
        "Percentage": r.percentage,
        "Amount": r.amount,
        "Status Joined": r.status_joined,
        "Entered By": r.entered_by,
      }));

      const ws =
        XLSX.utils.json_to_sheet(
          dataToExport
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Joining Report"
      );

      XLSX.writeFile(
        wb,
        "Joining_Report.xlsx"
      );
    };

  /* ================================================= */
  /* EXPORT PDF */
  /* ================================================= */

  const exportToPDF =
    () => {

      const doc =
        new jsPDF();

      doc.text(
        "Joining Report",
        14,
        15
      );

      doc.autoTable({

        head: [[

          "Candidate",

          "Company",

          "Position",

          "DOJ",

          "Status",
        ]],

        body:
          filteredData.map(
            (r) => ([

              r.candidate_name,

              r.company,

              r.position,

              dayjs(
                r.doj
              ).format(
                "YYYY-MM-DD"
              ),

              r.status_joined,
            ])
          ),
      });

      doc.save(
        "Joining_Report.pdf"
      );
    };

  /* ================================================= */
  /* OPEN MODAL */
  /* ================================================= */

  const openForm =
    (row = null) => {
      // console.log("Opening form with row:", row); // Debugging line

      if (row) {

        setEditingId(
          row.s_no // Use s_no as the unique ID for the placement_tracker entry
        );

        const formattedDOJ =
          row.doj
            ? dayjs(
              row.doj
            ).format(
              "YYYY-MM-DD"
            )
            : "";

        setFormData({
          ...row,
          doj: formattedDOJ,
          candidate_id: row.candidate_id || row.id || null,
          s_no: row.s_no,
          status_joined: String(row.status_joined || "").toUpperCase(),
        });

      } else {

        const today =
          dayjs();

        setEditingId(
          null
        ); // No ID for new entry

        setFormData({
          s_no: null, // No s_no for new entry
          candidate_id: null, // No linked candidate for a freshly added placement entry
          month: today.format("MMMM"),
          candidate_name: "",
          phone: "",
          company: "",
          position: "",
          designation: "",
          doj: today.format("YYYY-MM-DD"),
          ctc_offered: "",
          percentage: "",
          amount: "",
          status_joined: "",
          entered_by: `${user.first_name} ${user.last_name}`,
          user_id: user.id,
        });
      }

      setOpenModal(true);
    };
  /* ================================================= */
  /* SAVE */
  /* ================================================= */
  const saveForm = async () => {

    try {

      if (!formData.candidate_name.trim()) { // Trim to prevent empty string with spaces
        return Swal.fire(
          "Error",
          "Candidate name is required.",
          "error"
        );
      }

      if (!formData.status_joined.trim()) {
        return Swal.fire(
          "Error",
          "Please select a status.",
          "error"
        );
      }

      // Calculate amount if percentage and ctc_offered are available
      const calculatedAmount =
        (Number(formData.ctc_offered) || 0) * (Number(formData.percentage) || 0) / 100;

      const payload = {
        // Ensure all form data fields are included, with correct casing
        candidate_id: formData.candidate_id || null, // Pass candidate_id for potential candidate table update
        user_id: formData.user_id,
        month: formData.month,
        company: formData.company,
        candidate_name: formData.candidate_name,
        phone: formData.phone,
        position: formData.position,
        designation: formData.designation,
        doj: formData.doj,
        ctc_offered: Number(formData.ctc_offered) || 0, // Ensure numbers are passed
        percentage: Number(formData.percentage) || 0,
        amount: calculatedAmount, // Use the calculated amount
        status_joined: formData.status_joined.toUpperCase(),
        entered_by: `${user.first_name} ${user.last_name}`,
      };
      // console.log("Payload being sent:", payload); // Debugging line


      let result;

      if (
editingId !== null &&
editingId !== undefined
) {
        // console.log(`Attempting to UPDATE placementtracker/${editingId}`); // Debugging line
        result = await postData(
          `placementtracker/update/${editingId}`, // Use editingId (s_no)
          payload
        );
      } else {
        // console.log("Attempting to SUBMIT new placementtracker entry"); // Debugging line
        result = await postData(
          "placementtracker/submit",
          payload
        );
      }

      if (result?.status) {

        Swal.fire(
          "Success",
          result.message || "Operation completed successfully!",
          "success"
        );

        fetchData(); // Re-fetch data to update the table
        setOpenModal(false); // Close the modal

      } else {

        Swal.fire(
          "Error",
          result?.message || "Operation failed. Please try again.",
          "error"
        );
      }

    } catch (err) {

      console.error("Save form error:", err); // Use console.error for errors
      Swal.fire(
        "Error",
        "An unexpected error occurred while saving. " + (err.message || ""),
        "error"
      );
    }
  };

  /* ================================================= */
  /* DELETE */
  /* ================================================= */
const deleteRow = async (s_no) => {

if (
  !s_no ||
  s_no === undefined ||
  s_no === null
) {

return Swal.fire(
  "Error",
  "Invalid record ID.",
  "error"
);

}

try {

const deleteResult =
await postData(
`placementtracker/delete/${s_no}`,
{}
);

if(deleteResult?.status){

Swal.fire(
"Deleted!",
deleteResult.message,
"success"
);

fetchData();

}

else{

Swal.fire(
"Error",
deleteResult?.message,
"error"
);

}

}

catch(err){

console.log(err);

Swal.fire(
"Error",
"Delete failed",
"error"
);

}

};

  return (

    <div className="md:ml-72 pt-24 px-4 md:px-6 pb-8 min-h-screen text-slate-800">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="flex items-center justify-between mb-6">

        <div>

          <h1 className="text-2xl font-semibold">
            Joining Tracker
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Candidate joining analytics & reports
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => openForm(null)} // Button to add new entry
            className="
              bg-blue-600
              text-white
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            Add New Joining
          </button>

          <button
            onClick={exportToExcel}
            className="
              bg-black
              text-white
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            Excel
          </button>

          <button
            onClick={exportToPDF}
            className="
              border
              border-gray-300
              px-4
              py-2
              rounded-2xl
              text-sm
            "
          >
            PDF
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}

      <div className="glass-card p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">

          {/* SEARCH */}

          <div className="glass-input flex items-center gap-3 rounded-xl px-4 py-2.5">

            <FiSearch className="text-slate-400" />

            <input
              type="text"
              placeholder="Search..."
              value={
                filters.search
              }
              onChange={(e) => {
                setFilters({
                  ...filters,
                  search: e.target.value,
                });
                setPage(1); // Reset page on search
              }}
                className="w-full bg-transparent outline-none text-sm text-slate-800 dark:text-slate-200"
            />
          </div>

          {/* DATE */}

          <input
            type="date"
            value={
              filters.doj
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                doj: e.target.value,
              });
              setPage(1); // Reset page on date filter
            }}
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          />

          {/* COMPANY */}

          <select
            value={
              filters.company
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                company: e.target.value,
              });
              setPage(1); // Reset page on company filter
            }}
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          >

            <option value="">
              All Company
            </option>

            {companies.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* POSITION */}

          <select
            value={
              filters.position
            }
            onChange={(e) => {
              setFilters({
                ...filters,
                position: e.target.value,
              });
              setPage(1); // Reset page on position filter
            }}
            className="
              border
              border-gray-200
              rounded-2xl
              px-4
              py-3
              text-sm
              bg-[#fafafa]
            "
          >

            <option value="">
              All Position
            </option>

            {positions.map(
              (
                item,
                index
              ) => (

                <option
                  key={index}
                  value={item}
                >
                  {item}
                </option>
              )
            )}
          </select>

          {/* STATUS */}

          <select
            value={filters.status}
            onChange={(e) => {
              setFilters({
                ...filters,
                status: e.target.value,
              });
              setPage(1); // Reset page on status filter
            }}
            className="glass-input px-4 py-2.5 rounded-2xl text-sm outline-none"
          >

            <option value="">
              Select Status
            </option>

            <option value="JOINED">
              JOINED
            </option>

            <option value="OFFERED">
              OFFERED
            </option>

            <option value="REJECTED">
              REJECTED
            </option>

            <option value="BACKOUT">
              BACKOUT
            </option>

          </select>
        </div>

        <div className="flex justify-end mt-4">

          <button
            onClick={
              resetFilters
            }
            className="
              border
              border-gray-300
              px-5
              py-2
              rounded-2xl
              text-sm
            "
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* ================================================= */}
      {/* TABLE */}
      {/* ================================================= */}

      <div className="glass-card overflow-hidden">

        <div className="overflow-x-auto">

          <table className="w-full">

            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">

              <tr>

                {[
                  "Candidate",
                  "Company",
                  "Position",
                  "Joining",
                  "Revenue",
                  "Status",
                  "Action",
                ].map(
                  (head) => (

                    <th
                      key={head}
                      className="
                        px-5
                        py-4
                        text-left
                        text-xs
                        font-semibold
                        text-gray-500
                      "
                    >
                      {head}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>

                  <td
                    colSpan={7}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    Loading...
                  </td>
                </tr>

              ) : paginated.length === 0 ? (

                <tr>

                  <td
                    colSpan={7}
                    className="
                      text-center
                      py-10
                      text-gray-500
                    "
                  >
                    No Data Found
                  </td>
                </tr>

              ) : (

                paginated.map(
                  (
                    row,
                    index
                  ) => (

                    <tr
                      key={index}
                      className="
                        border-b
                        border-gray-100
                        hover:bg-[#fafafa]
                        transition-all
                      "
                    >

                      {/* CANDIDATE */}

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div
                            className="
                              w-10
                              h-10
                              rounded-2xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FaUserTie />
                          </div>

                          <div>

                            <h3 className="text-sm font-medium">
                              {
                                row.candidate_name
                              }
                            </h3>

                            <p className="text-xs text-gray-500">
                              {
                                row.phone
                              }
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* COMPANY */}

                      <td className="px-5 py-4 text-sm">

                        <div className="flex items-center gap-2">

                          <FaBuilding className="text-gray-400" />

                          {row.company || '-'}
                        </div>
                      </td>

                      {/* POSITION */}

                      <td className="px-5 py-4">

                        <div>

                          <h3 className="text-sm font-medium">
                            {
                              row.position || '-'
                            }
                          </h3>

                          <p className="text-xs text-gray-500">
                            {
                              row.designation || '-'
                            }
                          </p>
                        </div>
                      </td>
                      {/* JOINING */}

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm">

                            <FaCalendarAlt className="text-gray-400" />

                            {
                              row.doj
                                ? dayjs(
                                  row.doj
                                ).format(
                                  "YYYY-MM-DD"
                                )
                                : "-"
                            }
                          </div>

                          <p className="text-xs text-gray-500">
                            {row.month || "-"}
                          </p>
                        </div>
                      </td>

                      {/* REVENUE */}

                      <td className="px-5 py-4">

                        <div className="space-y-1">

                          <div className="flex items-center gap-2 text-sm font-semibold text-green-700">

                            <FaMoneyBillWave className="text-green-600" />

                            ₹
                            {Number(
                              row.amount && Number(row.amount) > 100
                                ? row.amount
                                : ((Number(row.ctc_offered || 0) * Number(row.percentage || 8.33)) / 100)
                            ).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>

                          <p className="text-xs text-gray-500">
                            CTC:
                            {" "}
                            ₹
                            {Number(
                              row.ctc_offered || 0
                            ).toLocaleString()}
                          </p>
                        </div>
                      </td>

                      {/* STATUS */}

                      <td className="px-5 py-4">

                        {(() => {
                          // Use the already formatted status_joined from state
                          const status = String(row.status_joined || "").toUpperCase();

                          return (

                            <span
                              className={`
          inline-flex
          items-center
          justify-center

          min-w-[90px]

          px-3
          py-1.5

          rounded-full

          text-[11px]
          font-semibold
          tracking-wide

          ${status === "JOINED"

                                  ? "bg-green-100 text-green-700 border border-green-200"

                                  : status === "OFFERED"

                                    ? "bg-blue-100 text-blue-700 border border-blue-200"

                                    : status === "REJECTED"

                                      ? "bg-red-100 text-red-700 border border-red-200"

                                      : status === "BACKOUT"

                                        ? "bg-yellow-100 text-yellow-700 border border-yellow-200"

                                        : "bg-gray-100 text-gray-700 border border-gray-200"
                                }
        `}
                            >
                              {status || "PENDING"}
                            </span>
                          );
                        })()}
                      </td>

                      {/* ACTION */}

                      <td className="px-5 py-4">

                        <div className="flex gap-2">

                          <button
                            onClick={() =>
                              openForm(
                                row
                              )
                            }
                            className="
                              w-9
                              h-9
                              rounded-xl
                              bg-black
                              text-white
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiEdit size={14} />
                          </button>

                          <button
                            onClick={() =>
                              deleteRow(
                                row.s_no // Always use row.s_no for deletion
                              )
                            }
                            className="
                              w-9
                              h-9
                              rounded-xl
                              border
                              border-red-200
                              text-red-500
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================= */}
      {/* PAGINATION */}
      {/* ================================================= */}

      <div className="flex justify-end gap-3 mt-5">

        {page > 1 && (

          <button
            onClick={() => setPage(page - 1)}
            className="glass-btn px-4 py-2 rounded-2xl text-sm"
          >
            Prev
          </button>
        )}

        {page *
          rowsPerPage <
          filteredData.length && (

            <button
              onClick={() => setPage(page + 1)}
              className="glass-btn px-4 py-2 rounded-2xl text-sm"
            >
              Next
            </button>
          )}
      </div>

      {/* ================================================= */}
      {/* MODAL */}
      {/* ================================================= */}

      {openModal && (

        <div
          className="
            fixed
            inset-0
            bg-black/50
            z-50
            flex
            items-center
            justify-center
            p-4
          "
        >

          <div
            className="
              bg-white
              w-full
              max-w-3xl
              rounded-3xl
              p-6
              relative
            "
          >

            <button
              onClick={() =>
                setOpenModal(false)
              }
              className="
                absolute
                top-5
                right-5
                text-gray-500
                hover:text-gray-700
              "
            >
              <FiX size={22} />
            </button>

            <h2 className="text-xl font-semibold mb-6">
              {editingId
                ? "Update Joining"
                : "Add Joining"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* Changed to md:grid-cols-2 for better responsiveness */}

              {/* MONTH */}

              <div>

                <label className="text-xs text-gray-500">
                  Month
                </label>

                <select
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                    bg-white
                    outline-none
                  "
                  value={
                    formData.month
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      month:
                        e.target.value,
                    })
                  }
                >

                  <option value="">
                    Select Month
                  </option>

                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((month) => (

                    <option
                      key={month}
                      value={month}
                    >
                      {month}
                    </option>
                  ))}
                </select>
              </div>

              {/* CANDIDATE */}

              <div>

                <label className="text-xs text-gray-500">
                  Candidate Name
                </label>

                <input
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.candidate_name
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      candidate_name:
                        e.target.value,
                    })
                  }
                  required // HTML5 validation
                />
              </div>

              {/* PHONE */}

              <div>

                <label className="text-xs text-gray-500">
                  Phone
                </label>

                <input
                  type="number"
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.phone
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      phone:
                        e.target.value,
                    })
                  }
                />
              </div>

              {/* COMPANY */}

              <div>

                <label className="text-xs text-gray-500">
                  Company
                </label>

                <input
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.company
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      company:
                        e.target.value,
                    })
                  }
                />
              </div>

              {/* POSITION */}

              <div>

                <label className="text-xs text-gray-500">
                  Position
                </label>

                <input
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.position
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      position:
                        e.target.value,
                    })
                  }
                />
              </div>

              {/* DESIGNATION */}

              <div>

                <label className="text-xs text-gray-500">
                  Designation
                </label>

                <input
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.designation
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      designation:
                        e.target.value,
                    })
                  }
                />
              </div>

              {/* DOJ */}

              <div>

                <label className="text-xs text-gray-500">
                  Date of Joining
                </label>

                <input
                  type="date"
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.doj
                  }
                  onChange={(e) => {

                    const selectedDate =
                      e.target.value;

                    setFormData({

                      ...formData,

                      doj:
                        selectedDate,

                      // Automatically update month based on selected DOJ
                      month:
                        selectedDate
                          ? dayjs(selectedDate).format("MMMM")
                          : "",
                    });
                  }}
                />
              </div>

              {/* CTC */}

              <div>

                <label className="text-xs text-gray-500">
                  CTC Offered
                </label>

                <input
                  type="number"
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.ctc_offered
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      ctc_offered:
                        e.target.value,
                    })
                  }
                  min="0"
                />
              </div>

              {/* PERCENTAGE */}

              <div>

                <label className="text-xs text-gray-500">
                  Percentage (%)
                </label>

                <input
                  type="number"
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    formData.percentage
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      percentage:
                        e.target.value,
                    })
                  }
                  min="0"
                  max="100"
                />
              </div>

              {/* AMOUNT */}

              <div>

                <label className="text-xs text-gray-500">
                  Amount
                </label>

                <input
                  type="number"
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                  "
                  value={
                    // Re-calculate amount dynamically based on CTC and Percentage for display
                    (Number(formData.ctc_offered) || 0) * (Number(formData.percentage) || 0) / 100
                  }
                  readOnly // This field is calculated, not directly editable
                  tabIndex="-1" // Prevent tabbing to this read-only field
                  min="0"
                />
              </div>

              {/* STATUS */}

              <div className="col-span-1 md:col-span-2"> {/* Make status field span full width on small screens, half on medium */}

                <label className="text-xs text-gray-500">
                  Status
                </label>

                <select
                  className="
                    w-full
                    mt-1
                    border
                    border-gray-200
                    rounded-2xl
                    px-4
                    py-3
                    text-sm
                    bg-white
                    outline-none
                  "
                  value={
                    formData.status_joined
                  }
                  onChange={(e) =>
                    setFormData({

                      ...formData,

                      status_joined:
                        e.target.value,
                    })
                  }
                  required
                >

                  <option value="">
                    Select Status
                  </option>

                  <option value="JOINED">
                    JOINED
                  </option>

                  <option value="OFFERED">
                    OFFERED
                  </option>

                  <option value="REJECTED">
                    REJECTED
                  </option>

                  <option value="BACKOUT">
                    BACKOUT
                  </option>
                </select>
              </div>
            </div>

            <button
              onClick={saveForm}
              className="
                mt-6
                w-full
                bg-black
                text-white
                py-3
                rounded-2xl
                font-medium
                hover:bg-gray-800
                transition-colors
              "
            >
              {editingId
                ? "Update Joining"
                : "Save Joining"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}